import { Check, Loader2 } from 'lucide-react'
import type { QuoteFooterSubtotals } from 'src/shared/components/OrderTable/utils'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { formatProposalMoney } from '../utils'

export type ProposalApprovalState = 'in_review' | 'ready' | 'approved'

interface ProposalSummaryProps {
  subtotals: QuoteFooterSubtotals
  total: number
  currency: string
  approvalState: ProposalApprovalState
  /** Staff viewing the share link see the summary but cannot approve.
   *  Visitors who are not signed in can press it: it starts sign-in first. */
  canApprove: boolean
  isApproving: boolean
  onApprove: () => void
}

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Row className="justify-between gap-4">
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </Row>
)

export const ProposalSummary = ({
  subtotals,
  total,
  currency,
  approvalState,
  canApprove,
  isApproving,
  onApprove,
}: ProposalSummaryProps) => {
  const { t } = useLocalization()
  const money = (amount: number) => formatProposalMoney(amount, currency)
  const products = subtotals.productBase + subtotals.productCustomLines
  const other = subtotals.customQuoteItems + subtotals.otherSubItems

  return (
    <aside
      aria-label={t('Your order', 'Din order')}
      className="box-content flex min-w-0 flex-[1_1_17rem] flex-col gap-3.5 rounded-[20px] border border-[var(--raised-border)] bg-proposal-surface shadow-soft p-6 lg:sticky lg:top-6"
    >
      <span className="text-[11.5px] uppercase tracking-[0.15em] text-proposal-ink-muted">
        {t('Your order', 'Din order')}
      </span>
      <Column className="gap-[7px] text-sm text-proposal-ink-soft">
        <SummaryRow label={t('Products', 'Produkter')} value={money(products)} />
        <SummaryRow
          label={t('Decoration', 'Dekoration')}
          value={money(subtotals.decoration)}
        />
        <SummaryRow label={t('Setup', 'Uppstart')} value={money(subtotals.setupCost)} />
        {other > 0 ? (
          <SummaryRow label={t('Other', 'Övrigt')} value={money(other)} />
        ) : null}
        {/* Freight depends on the order: show the quoted amount, never assume it is included. */}
        <SummaryRow
          label={t('Freight', 'Frakt')}
          value={
            subtotals.shipping > 0
              ? money(subtotals.shipping)
              : t('Not quoted yet', 'Ej offererad än')
          }
        />
      </Column>
      <Row className="flex-wrap items-baseline justify-between gap-y-1 border-t border-proposal-ink/[0.12] pt-3.5">
        <span className="text-[15px] font-semibold text-proposal-ink">
          {t('Total excl. VAT', 'Totalt exkl. moms')}
        </span>
        <span className="whitespace-nowrap text-[22px] font-semibold tabular-nums text-proposal-ink">
          {money(total)}
        </span>
      </Row>

      {approvalState === 'approved' ? (
        <Row className="mt-1.5 items-start gap-3 rounded-[16px] border border-emerald-200 bg-gradient-to-br from-emerald-50/90 via-white to-sky-50/50 p-4">
          <div className="grid size-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
            <Check className="size-4" strokeWidth={2.5} aria-hidden />
          </div>
          <Column className="gap-1">
          <span className="text-[15px] font-semibold tracking-[-0.01em] text-proposal-ink">
            {t('Approved — thank you', 'Godkänt — tack')}
          </span>
          <span className="text-[13px] leading-normal text-proposal-ink-soft">
            {t(
              'We are ordering now. Factory proofs come to you for sign-off before anything is printed.',
              'Vi beställer nu. Fabrikskorrektur kommer till er för godkännande innan något trycks.'
            )}
          </span>
          </Column>
        </Row>
      ) : approvalState === 'in_review' ? (
        <Column className="mt-1.5 gap-1 rounded-[16px] border border-[var(--raised-border)] bg-proposal-ground p-4">
          <span className="text-[15px] font-semibold tracking-[-0.01em] text-proposal-ink">
            {t('Klayder is reviewing', 'Klayder granskar')}
          </span>
          <span className="text-[13px] leading-normal text-proposal-ink-soft">
            {t(
              'We check pricing, sizes and colours. Then you can approve.',
              'Vi kollar pris, storlekar och färger. Sedan kan ni godkänna.'
            )}
          </span>
        </Column>
      ) : (
        <Column className="gap-2.5 pt-1.5">
          <Button
            variant="primaryOrange"
            size="lg"
            onClick={onApprove}
            disabled={!canApprove || isApproving}
            className="w-full gap-2 px-6 text-[15px] font-semibold"
          >
            {isApproving ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : null}
            {t('Approve this proposal', 'Godkänn förslaget')}
          </Button>
          <p className="m-0 text-[12.5px] leading-normal text-proposal-ink-muted">
            {t(
              'Approving does not charge anything. You see factory proofs before production starts.',
              'Att godkänna kostar ingenting. Ni ser fabrikskorrektur innan produktionen startar.'
            )}
          </p>
        </Column>
      )}
    </aside>
  )
}
